import {
  Alert,
  Button,
  Flex,
  Heading,
  IconAddLine,
  Pages,
  View,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { PlanTemplateService } from "../../services/PlanTemplateService";
import { sort } from "../../utils/utils";
import generateUuid from "../../utils/Uuid";
import { CANVAS, EXTERNAL } from "../Assessment/Plan/utils";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import ManageTemplate from "./ManageTemplate";
import TemplateCard from "./TemplateCard";

const PlanTemplates = () => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (selectedTemplate) {
      setActivePageIndex(1);
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (activePageIndex === 0) {
      setSelectedTemplate(null);
    }
  }, [activePageIndex]);

  useEffect(() => {
    async function fetchTemplates() {
      try {
        const templateList = await PlanTemplateService.getTemplates();
        templateList.forEach((template) => {
          template.sections.forEach((section, i) => {
            section.index = i;
            section.measures.forEach((measure, j) => {
              measure.index = j;
              // Supply a description key for old templates that don't have one
              if (
                (measure.type === CANVAS || measure.type === EXTERNAL) &&
                !measure.description_key
              ) {
                measure.description_key = generateUuid();
              }
            });
          });
        });
        setTemplates(sort(templateList, (template) => template.name));
      } catch {
        showNotification(
          t("notifications.general.fetchError", { obj: "Templates" }),
          ERROR
        );
      } finally {
        setLoading(false);
      }
    }
    fetchTemplates();
  }, [showNotification]);

  return (
    <View as="div" minHeight="calc(100vh - 2.375rem - 1.5rem - 3rem)">
      <Pages
        activePageIndex={activePageIndex}
        onPageIndexChange={(index) => setActivePageIndex(index)}
      >
        <Pages.Page padding="none">
          <div style={{ marginBottom: "15px", borderBottom: "1px solid #ccc" }}>
            <Flex justifyItems="space-between" alignItems="end">
              <Flex.Item shouldGrow>
                <Heading as="h2" level="h3">
                  {t("planTemplates.planTemplates")}
                </Heading>
              </Flex.Item>
              <Flex.Item>
                <Button
                  id="create-template-button"
                  color="primary"
                  renderIcon={IconAddLine}
                  onClick={() => setActivePageIndex(1)}
                >
                  {t("templates.createTemplate")}
                </Button>
              </Flex.Item>
            </Flex>
          </div>
          {loading ? (
            <Skeleton
              count={3}
              style={{ height: 106, borderRadius: 5, marginBottom: 10 }}
            />
          ) : templates.length > 0 ? (
            <Flex wrap="wrap" alignItems="start">
              {templates.map((template, key) => (
                <Flex.Item
                  size="50%"
                  key={key}
                  padding={
                    key % 2 === 0
                      ? "0 x-small x-small 0"
                      : "0 0 x-small x-small"
                  }
                >
                  <TemplateCard
                    template={template}
                    setTemplates={setTemplates}
                    setSelectedTemplate={setSelectedTemplate}
                  />
                </Flex.Item>
              ))}
            </Flex>
          ) : (
            <Alert>{t("templates.noTemplatesAlert")}</Alert>
          )}
        </Pages.Page>
        <Pages.Page padding="none">
          {(_history, navigateToPreviousPage) => (
            <View as="div" minHeight="calc(100vh - 2.375rem - 1.5rem - 3rem)">
              <ManageTemplate
                template={selectedTemplate}
                navigateToPreviousPage={navigateToPreviousPage}
                setTemplates={setTemplates}
              />
            </View>
          )}
        </Pages.Page>
      </Pages>
    </View>
  );
};

export default PlanTemplates;
